import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Icon } from '@pwc/pwc-deals-ui';
import {
  Constants,
  NavigationNodeId,
  USER_ROLES,
} from '@pwc/pwc-deals-web-common';
import {
  getIsThirdPartyMember,
  getUserRole,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';

import TagList from 'features/analyses/common/TagList/TagList';
import AvatarWithMenu from 'common/components/AvatarWithMenu';
import VersionStatus from 'common/components/Pills/VersionStatus';
import { DraftPillClientView } from 'common/components/Pills/DraftPill';
import featureFlags from 'common/helpers/featureFlags';
import GroupsPermissions from '../GroupsPermissions/GroupsPermissions';
import AnalysisMenu from '../AnalysisMenu/AnalysisMenu';
import {
  getNavigationLink,
  geKFAtNavigationLink,
} from '../../utils/groupsUtilities';
import './AnalysisItem.scss';
import { getProject } from '../../../project/projectSlice';
import { useKFATitleI18n } from '../../../../common/hooks/useKFATitleI18n';
import { useFormatDateI18n } from '../../../../common/hooks/useFomatDateI18n';

const AnalysisItem = ({
  projectId,
  contentId,
  index,
  item,
  handleDelete,
  handleMove,
  isNoneGroup = false,
  isDraggable,
  permissions,
}) => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const { navigation = [] } = useSelector(getProject);
  const { t } = useTranslation();
  const kfaNavigationData = useMemo(
    () => navigation.find((nav) => nav.id === NavigationNodeId.KEY_FOCUS_AREAS),
    [navigation]
  );
  const KFATitleI18n = useKFATitleI18n(kfaNavigationData?.title);
  const {
    contentThreadId,
    title,
    versionNumber,
    versionStatus,
    dateModified,
    modifiedBy,
    tags,
    kfa,
    subType,
    type,
  } = item;

  const isThirdPartyMember = useSelector(getIsThirdPartyMember);

  const { hasPermission, isClientUser, isPwcReadOnlyRole } = permissions;

  const formattedDateModifiedI18n = useFormatDateI18n(dateModified);

  let analysisItemClass = 'analysis-item';

  if (isClientUser) {
    analysisItemClass = 'analysis-item-client-view';
  } else if (isPwcReadOnlyRole) {
    analysisItemClass = 'analysis-item-ready-only-view';
  }

  const AnalysisItemUi = (dragProps = {}) => {
    return (
      <div
        className={analysisItemClass}
        data-testid={`collapse-group-row-${index}`}
      >
        <div {...dragProps} className={`${analysisItemClass}__main`}>
          <div className={`${analysisItemClass}__titular`}>
            <h4>
              <Link
                to={`${getNavigationLink(
                  projectId,
                  contentThreadId,
                  subType,
                  type,
                  isPwcUser
                )}`}
              >
                {title}
              </Link>
            </h4>

            {tags && tags.length > 0 && (
              <TagList tags={tags} testId="analysis-tag-list" iconSize={18} />
            )}

            {!kfaNavigationData?.isHidden && kfa && (
              <div className="kfa">
                <Icon name="hypothesis" width="18" />
                <Link
                  className="kfa__title"
                  to={`${geKFAtNavigationLink(projectId, kfa.contentThreadId)}`}
                >
                  {kfa.title}
                </Link>
              </div>
            )}
            {!kfaNavigationData?.isHidden &&
              !kfa &&
              (contentId === -1 || isNoneGroup) && (
                <div className="kfa" data-testid="no-kfa">
                  <Icon name="hypothesis" width="18" />
                  <span className="kfa__no-kfa-title">
                    {t('noKeyFocusAreaConnected', {
                      KFATitle: KFATitleI18n,
                    })}
                  </span>
                </div>
              )}
          </div>

          {!isThirdPartyMember && (
            <div className={`${analysisItemClass}__modified`}>
              <h4>
                {versionStatus === Constants.VERSION_STATUS.SHARED.value
                  ? t('lastShared')
                  : t('lastUpdated')}
              </h4>
              <section>{formattedDateModifiedI18n}</section>
              {isClientUser ? null : <section>{modifiedBy}</section>}
            </div>
          )}
        </div>

        <div className={`${analysisItemClass}__details`}>
          {!isThirdPartyMember && (
            <div className={`${analysisItemClass}__versions`}>
              {isClientUser ? (
                <DraftPillClientView />
              ) : (
                <VersionStatus
                  content={{ versionStatus, versionNumber }}
                  allowDraftPill
                  hideTextVersion={featureFlags.isVersioningPhase1Enabled}
                />
              )}
            </div>
          )}
          {hasPermission && (
            <GroupsPermissions projectId={projectId}>
              <AvatarWithMenu contentThreadId={contentThreadId}>
                <AnalysisMenu
                  projectId={projectId}
                  handleDelete={handleDelete}
                  handleMove={handleMove}
                  analysisId={contentThreadId}
                  subType={subType}
                  type={type}
                />
              </AvatarWithMenu>
            </GroupsPermissions>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {isDraggable && (
        <Draggable
          key={String(contentThreadId)}
          draggableId={String(contentThreadId)}
          index={index}
        >
          {(provided) => {
            const dragProps = hasPermission ? provided.dragHandleProps : {};
            return (
              <div ref={provided.innerRef} {...provided.draggableProps}>
                {AnalysisItemUi(dragProps)}
              </div>
            );
          }}
        </Draggable>
      )}
      {!isDraggable && <AnalysisItemUi />}
    </>
  );
};

export default AnalysisItem;
